import { useEffect, useMemo, useState } from 'react';
import { usePmsIntegrationEnabled } from '@/features/payments/hooks/usePmsIntegrationEnabled';
import { useLazyGetMaintenanceListQuery, IMaintenanceListResponse } from '@/store/api/maintenanceApi';

export const useMaintenanceTickets = () => {
  const pmsEnabled = usePmsIntegrationEnabled();
  const [getMaintenanceList, { data, error, isLoading, isFetching, isUninitialized }] = useLazyGetMaintenanceListQuery();
  const [openTickets, setOpenTickets] = useState<IMaintenanceListResponse[] | undefined>();
  const [closedTickets, setClosedTickets] = useState<IMaintenanceListResponse[] | undefined>();
  const [allTickets, setAllTickets] = useState<IMaintenanceListResponse[] | undefined>();

  useEffect(() => {
    if (pmsEnabled) {
      getMaintenanceList();
    }
  }, [getMaintenanceList, pmsEnabled]);

  useEffect(() => {
    const tickets = data?.response || [];
    if (tickets) {
      // Align with iOS: sort primarily by updatedOn desc when both present.
      // If one is missing updatedOn, items WITH updatedOn should come first (newer),
      // and if both missing updatedOn, preserve existing order (no createdOn fallback).
      const byIosUpdatedDesc = (a: any, b: any) => {
        const aU = a.updatedOn ? new Date(a.updatedOn).getTime() : undefined;
        const bU = b.updatedOn ? new Date(b.updatedOn).getTime() : undefined;
        if (aU != null && bU != null) return bU - aU; // both have updatedOn
        if (aU != null && bU == null) return -1;      // a first
        if (aU == null && bU != null) return 1;       // b first
        return 0; // both missing: keep relative order
      };

      const open = tickets
        .filter((t) => !t.completedOn)
        .slice()
        .sort(byIosUpdatedDesc);
      const closed = tickets
        .filter((t) => !!t.completedOn)
        .slice()
        .sort(byIosUpdatedDesc);
      const all = [...tickets].sort(byIosUpdatedDesc);
      setOpenTickets(open);
      setClosedTickets(closed);
      setAllTickets(all);
    }
  }, [data]);

  const loading = useMemo(() => isLoading || isFetching || isUninitialized, [isLoading, isFetching, isUninitialized]);
  const refresh = () => getMaintenanceList();

  return { openTickets, closedTickets, allTickets, loading, error, refresh };
};


